import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { generateHotelPath, generateHotelSlug } from '../utils/slugUtils';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
// DB-FIRST: Destinations loaded from API only (no localStorage)
import { getHotelPriceCalendar, getProductInfo, fetchHotelsByDestination, fetchTourPackages } from '../services/tourvisioService';
import { getImagesForDestination } from '../services/imageService';
import { SearchWidget } from './SearchWidget';
import PageCTA from './PageCTA';
import { getDestinationAds } from '../services/dataService';
import { useLocalization } from '../hooks/useLocalization';
import { SearchType } from '../types';
import type { DestinationContent, SearchQuery, ApiLogEntry, FeaturedHotelConfig, Hotel, Widget } from '../types';
import { WidgetRenderer, HeroWidget, WeatherWidget, RichTextWidget, HotelGridWidget } from './DestinationWidgets';
import { ThingsToDoSection, AttractionsSection, ItinerariesSection, RestaurantsSection, PracticalInfoSection, HotelsSection, TourPackagesSection, AirportInfoSection } from './DestinationSections';
import { translateLocationName } from '../utils/localizationUtils';
// import { PriceCalendar } from './PriceCalendar'; // DISABLED — preserved for future use

// Blog Posts Section — fetches from destination-blog API
const BlogPostsSection: React.FC<{ destinationSlug: string; destinationName: string }> = ({ destinationSlug, destinationName }) => {
    const { language } = useLocalization();
    const navigate = useNavigate();
    const [posts, setPosts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`/api/destination-blog/public/${destinationSlug}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data?.success) setPosts(data.posts || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [destinationSlug]);

    if (!loading && posts.length === 0) return null;

    const getText = (val: any): string => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        if (val[language]) return val[language];
        if (val.en) return val.en;
        return '';
    };

    return (
        <section className="py-16 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4 mb-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-500/25">
                        <span className="material-icons-round text-white text-2xl">article</span>
                    </div>
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                            {language === 'sv' ? `Reseguider för ${destinationName}` : language === 'da' ? `Rejseguides for ${destinationName}` : `Travel Guides for ${destinationName}`}
                        </h2>
                        <p className="text-gray-600 text-sm">
                            {language === 'sv' ? 'Tips, inspiration och lokala hemligheter' : language === 'da' ? 'Tips, inspiration og lokale hemmeligheder' : 'Tips, inspiration and local secrets'}
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-gray-50 rounded-2xl overflow-hidden animate-pulse">
                                <div className="h-48 bg-gray-200" />
                                <div className="p-5 space-y-3">
                                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                                    <div className="h-4 bg-gray-200 rounded w-full" />
                                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {posts.slice(0, 6).map((post, i) => (
                            <div
                                key={post._id || i}
                                onClick={() => navigate(`/blog/${destinationSlug}/${post.slug || post._id}`)}
                                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:-translate-y-1"
                            >
                                <div className="relative h-48 overflow-hidden bg-gray-100">
                                    <img
                                        src={post.coverImage?.url || post.coverImage || 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=600'}
                                        alt={getText(post.title)}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    {post.category && (
                                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-rose-500 text-white text-xs font-bold rounded-full">
                                            {post.category}
                                        </span>
                                    )}
                                </div>
                                <div className="p-5">
                                    <h3 className="font-bold text-gray-900 text-lg line-clamp-2 mb-2 group-hover:text-rose-600 transition-colors">
                                        {getText(post.title)}
                                    </h3>
                                    <p className="text-gray-600 text-sm line-clamp-3 mb-3">
                                        {getText(post.excerpt) || getText(post.summary)}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-gray-400">
                                        <span className="material-icons-round text-xs">schedule</span>
                                        {post.readTime || '5 min'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {posts.length > 6 && (
                    <div className="text-center mt-8">
                        <button
                            onClick={() => navigate(`/blog/${destinationSlug}`)}
                            className="px-6 py-3 bg-rose-50 text-rose-600 font-bold rounded-full hover:bg-rose-100 transition-colors inline-flex items-center gap-2"
                        >
                            {language === 'sv' ? 'Se alla artiklar' : language === 'da' ? 'Se alle artikler' : 'View all articles'}
                            <span className="material-icons-round text-sm">arrow_forward</span>
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

interface DestinationLandingPageProps {
    logApiCall: (log: ApiLogEntry) => void;
    isSettingsReady: boolean;
}

interface FeaturedHotelDisplay {
    hotelId: string;
    name: string;
    imageUrl: string;
    price: number | null;
    stars: number;
    city: string;
    checkIn: string;
    duration: number;
}

export const DestinationLandingPage: React.FC<DestinationLandingPageProps> = ({ logApiCall, isSettingsReady }) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { language, currency } = useLocalization();
    const [destination, setDestination] = useState<DestinationContent | null>(null);
    const [featuredHotels, setFeaturedHotels] = useState<FeaturedHotelDisplay[]>([]);
    const [loadingHotels, setLoadingHotels] = useState(false);

    // API Hotels State
    const [apiHotels, setApiHotels] = useState<any[]>([]);
    const [loadingApiHotels, setLoadingApiHotels] = useState(false);

    // Tour Packages State (Holiday Packages with Flights)
    const [tourPackages, setTourPackages] = useState<any[]>([]);
    const [loadingTourPackages, setLoadingTourPackages] = useState(false);

    // Premium Hotels removed from destination pages — available on restaurant pages as 'nearby hotels'

    // Dynamic Images State
    // Dynamic Ads State
    const [ads, setAds] = useState<any[]>([]);
    const [apiImages, setApiImages] = useState<string[]>([]);

    // Helper to safely extract text from potentially localized objects
    const mapBackendDestination = useCallback((dest: any): DestinationContent => {
        // Safe text extraction helper
        const getText = (val: any, lang: 'en' | 'sv' | 'da' = 'en'): string => {
            if (!val) return '';
            if (typeof val === 'string') return val;
            if (typeof val === 'object' && val[lang]) return val[lang];
            if (typeof val === 'object' && val.en) return val.en;
            if (typeof val === 'object' && val.sv) return val.sv;
            return '';
        };

        // Map approved TripAdvisor attractions to frontend format
        const attractions = (dest.tripAdvisorAttractions || [])
            .filter((a: any) => a.approved)
            .map((a: any) => ({
                id: a.externalId || a._id,
                title: { en: a.name, sv: a.name },
                description: { en: a.snippet || '', sv: a.snippet || '' },
                image: a.thumbnail || '',
                category: 'attraction',
                rating: a.rating,
                entryFee: null
            }));

        // Map approved TripAdvisor restaurants to frontend format
        const restaurants = (dest.tripAdvisorRestaurants || [])
            .filter((r: any) => r.approved)
            .map((r: any) => ({
                id: r.externalId || r._id,
                name: r.name,
                description: { en: r.snippet || '', sv: r.snippet || '' },
                image: r.thumbnail || '',
                cuisine: (r.cuisines || []).join(', ') || 'Restaurant',
                priceRange: r.priceLevel || '$$$',
                rating: r.rating
            }));

        // Map serpApiImages to gallery
        const serpImages = (dest.serpApiImages || [])
            .filter((img: any) => img.approved)
            .map((img: any) => img.url);

        // Extract description strings safely (description can be {en, sv} object or string)
        const descEn = getText(dest.description, 'en');
        const descSv = getText(dest.description, 'sv') || getText(dest.descriptionSv) || descEn;
        const descDa = getText(dest.description, 'da') || getText(dest.descriptionDa) || descEn;

        return {
            id: dest._id || dest.id,
            destinationId: dest.tourvisioId || dest._id || dest.id,
            title: getText(dest.name) || getText(dest.title),
            titleSv: getText(dest.nameSv) || getText(dest.titleSv) || getText(dest.name),
            imageUrl: dest.landingPage?.heroImage || dest.heroImage || dest.imageUrl || serpImages[0] || '',
            heroImage: dest.landingPage?.heroImage || dest.heroImage || serpImages[0],
            coverImage: dest.coverImage,
            price: dest.price || '',
            type: dest.type || '',
            isActive: dest.isActive ?? true,
            isFeatured: dest.featured || dest.isFeatured,
            landingPage: dest.landingPage ? {
                heroImage: dest.landingPage.heroImage || dest.heroImage || serpImages[0],
                description: {
                    en: getText(dest.landingPage.description, 'en') || descEn,
                    sv: getText(dest.landingPage.description, 'sv') || descSv,
                    da: getText(dest.landingPage.description, 'da') || descDa
                },
                featuredHotels: dest.landingPage.featuredHotels || []
            } : {
                heroImage: dest.heroImage || serpImages[0],
                description: { en: descEn, sv: descSv, da: descDa },
                featuredHotels: dest.featuredHotels || []
            },
            richContent: dest.richContent,
            gallery: serpImages.length > 0 ? serpImages : dest.gallery,
            weather: dest.weather,
            layout: dest.layout,
            sections: {
                thingsToDo: dest.sections?.thingsToDo || [],
                attractions: attractions.length > 0 ? attractions : (dest.sections?.attractions || []),
                restaurants: restaurants.length > 0 ? restaurants : (dest.sections?.restaurants || []),
                itineraries: dest.sections?.itineraries || [],
                practicalInfo: dest.sections?.practicalInfo
            },
            tourSearchConfig: dest.tourSearchConfig || undefined,
            hotelWhitelist: dest.hotelWhitelist || undefined,
            ctaBar: dest.ctaBar || undefined,
            sectionConfig: dest.sectionConfig || [],
            airportInfo: dest.airportInfo || undefined
        };
    }, []);

    const refreshData = useCallback(async () => {
        // DB-FIRST: Load destination exclusively from MongoDB API
        try {
            const response = await fetch(`/api/destinations/${slug}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.destination) {
                    const mapped = mapBackendDestination(data.destination);
                    // Clean up empty layout arrays so fallback content renders properly
                    if (mapped.layout && Array.isArray(mapped.layout) && mapped.layout.length === 0) {
                        mapped.layout = undefined;
                    }
                    setDestination(mapped);
                    return;
                }
            }
        } catch (e) {
            console.warn('[DestinationLandingPage] Backend fetch failed:', e);
        }

        // No destination found in DB
        navigate('/');
    }, [slug, navigate, mapBackendDestination]);


    useEffect(() => {
        refreshData();

        // Listen for updates from Admin
        window.addEventListener('tourvisio_data_update', refreshData);
        window.addEventListener('storage', refreshData);
        return () => {
            window.removeEventListener('tourvisio_data_update', refreshData);
            window.removeEventListener('storage', refreshData);
        };
    }, [refreshData]);

    // Fetch Dynamic Images
    useEffect(() => {
        if (!destination) return;
        const fetchImages = async () => {
            const searchTerm = `${destination.title} travel landmark scenery`;
            const images = await getImagesForDestination(searchTerm, 5);
            setApiImages(images);
        };
        fetchImages();
    }, [destination?.title]);

    // SEO Meta Tags - Dynamic title, description, og:tags
    useEffect(() => {
        if (!destination) return;

        const translatedCity = language === 'sv' && destination.titleSv
            ? destination.titleSv
            : translateLocationName(destination.title, language);

        const title = language === 'sv'
            ? `${translatedCity} Resor | Lumi Tours`
            : language === 'da'
            ? `${translatedCity} Rejser | Lumi Tours`
            : `${translatedCity} Holidays | Lumi Tours`;

        const description = language === 'sv'
            ? `Boka din resa till ${translatedCity}. Hitta paketresor, hotell och flyg till ${translatedCity} med Lumi Tours.`
            : language === 'da'
            ? `Book din rejse til ${translatedCity}. Find pakkerejser, hoteller og fly til ${translatedCity} med Lumi Tours.`
            : `Book your trip to ${translatedCity}. Find package holidays, hotels and flights to ${translatedCity} with Lumi Tours.`;

        // Set document title
        document.title = title;

        // Helper to set/update meta tags
        const setMetaTag = (name: string, content: string, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            let meta = document.querySelector(`meta[${attr}="${name}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute(attr, name);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', content);
        };

        // Basic meta tags
        setMetaTag('description', description);
        setMetaTag('keywords', `${destination.title}, holidays, package tours, hotels, flights, travel, vacation`);

        // Open Graph tags
        setMetaTag('og:title', title, true);
        setMetaTag('og:description', description, true);
        setMetaTag('og:type', 'website', true);
        setMetaTag('og:url', `https://lumitours.se/destination/${slug}`, true);
        if (destination.landingPage?.heroImage || destination.imageUrl) {
            setMetaTag('og:image', destination.landingPage?.heroImage || destination.imageUrl, true);
        }

        // Twitter Card
        setMetaTag('twitter:card', 'summary_large_image');
        setMetaTag('twitter:title', title);
        setMetaTag('twitter:description', description);

        // Canonical URL
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', `https://lumitours.se/destination/${slug}`);

        // Cleanup on unmount
        return () => {
            document.title = 'Lumi Tours';
        };
    }, [destination, slug, language]);

    // Tour Search Config — admin-configurable per destination, with fallbacks
    const tsc = destination?.tourSearchConfig;
    const searchNights = tsc?.nights || 7;
    const searchAdults = tsc?.adults || 2;
    const searchDepartureId = tsc?.departureId || '10';
    const searchNationality = tsc?.nationality || 'SE';
    const searchFutureDays = tsc?.futureDays ?? 60;
    const searchCurrency = tsc?.currency || currency;
    const getSearchCheckIn = () => {
        if (tsc?.checkIn) return tsc.checkIn;
        return new Date(Date.now() + searchFutureDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    };

    // Hotel Whitelist — admin selects which hotels to show
    const hotelWhitelist = destination?.hotelWhitelist;
    const filterByWhitelist = (hotels: any[]) => {
        if (!hotelWhitelist || hotelWhitelist.length === 0) return hotels;
        const whitelistIds = new Set(hotelWhitelist.map(w => w.hotelId));
        return hotels.filter(h => whitelistIds.has(h.id || h.productId || ''));
    };

    // Fetch API Hotels for Destination
    useEffect(() => {
        if (!destination?.destinationId || !isSettingsReady) return;

        const fetchApiHotels = async () => {
            setLoadingApiHotels(true);
            try {
                const hotels = await fetchHotelsByDestination(destination.destinationId, logApiCall);
                // Map to display format
                const displayHotels = hotels.slice(0, 8).map(h => ({
                    id: h.id || h.productId || '',
                    name: h.name,
                    imageUrl: h.imageUrl || h.thumbnailUrl || '',
                    price: h.pricePerNight || null,
                    pricePerNight: h.pricePerNight || null,
                    totalPrice: (h as any).totalPrice || null,
                    stars: h.rating || 5,
                    city: h.location?.city || destination.title.split(',')[0],
                    board: h.board || '',
                    offers: h.offers || [],
                    searchId: (h as any).searchId,
                    provider: h.provider,
                    location: h.location,
                    rating: h.rating,
                    currency: h.currency,
                    roomType: h.roomType,
                    themes: h.themes,
                    facilities: h.facilities,
                    description: h.description,
                    images: h.images
                }));
                setApiHotels(filterByWhitelist(displayHotels));
            } catch (e) {
                console.error('Failed to fetch API hotels:', e);
            }
            setLoadingApiHotels(false);
        };
        fetchApiHotels();

        // Fetch Tour Packages (Holiday Packages with Flights)
        const fetchTourPkgs = async () => {
            setLoadingTourPackages(true);
            try {
                let checkInDate = destination.tourSearchConfig?.checkIn;
                if (!checkInDate) {
                    const futureDays = destination.tourSearchConfig?.futureDays ?? 60;
                    checkInDate = new Date(Date.now() + futureDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                }
                const packages = await fetchTourPackages(destination.destinationId, language, checkInDate, logApiCall, currency);
                setTourPackages(filterByWhitelist(packages));
            } catch (e) {
                console.error('Failed to fetch tour packages:', e);
            }
            setLoadingTourPackages(false);
        };
        fetchTourPkgs();

        // Premium Hotels removed from destination pages — see restaurant pages for nearby hotels

        const fetchAds = async () => {
            const destAds = await getDestinationAds(destination.destinationId);
            setAds(destAds);
        };
        fetchAds();
    }, [destination?.destinationId, isSettingsReady, logApiCall, language, currency]);

    // Fetch Featured Hotels
    useEffect(() => {
        if (!destination || !destination.landingPage?.featuredHotels || !isSettingsReady) return;

        const fetchFeatured = async () => {
            setLoadingHotels(true);
            const configs = destination.landingPage?.featuredHotels || [];
            const results: FeaturedHotelDisplay[] = [];
            const STOCKHOLM_DEPARTURE_ID = "10";
            const FUTURE_DAYS = 60;
            const DURATION = 7;
            const ADULTS = 2;

            for (const config of configs) {
                try {
                    const info = await getProductInfo(2, config.hotelId, 1, 'en-US', (log) => { });

                    if (info) {
                        const checkInDate = new Date();
                        checkInDate.setDate(checkInDate.getDate() + FUTURE_DAYS);
                        const checkInStr = checkInDate.toISOString().split('T')[0];

                        const query: SearchQuery = {
                            type: SearchType.PackageTours,
                            departureId: STOCKHOLM_DEPARTURE_ID,
                            destinationId: destination.destinationId,
                            destinationType: 2,
                            checkIn: checkInStr,
                            nights: DURATION,
                            rooms: 1,
                            nationality: 'SE',
                            currency: currency,
                            adults: ADULTS,
                            children: 0,
                            childrenAges: []
                        };

                        const prices = await getHotelPriceCalendar(query, config.hotelId, (log) => { });
                        const bestPrice = prices.length > 0 ? prices[0].price : null;

                        results.push({
                            hotelId: config.hotelId,
                            name: info.description.split('\n')[0] || "Luxury Hotel",
                            imageUrl: info.images[0] || "",
                            stars: 5,
                            city: destination.title.split(',')[0],
                            price: bestPrice,
                            checkIn: checkInStr,
                            duration: DURATION
                        });
                    }
                } catch (e) {
                    console.error(`Failed to fetch featured hotel ${config.hotelId}`, e);
                }
            }
            setFeaturedHotels(results);
            setLoadingHotels(false);
        };

        fetchFeatured();
    }, [destination, isSettingsReady, language, currency]);

    const handleSearch = (query: SearchQuery) => {
        navigate('/results', { state: { searchQuery: query, from: `/destination/${slug}` } });
    };

    const handleTourPackageClick = (pkg: any) => {
        const query: SearchQuery = {
            searchId: pkg.searchId,
            type: SearchType.PackageTours,
            destinationId: destination?.destinationId || '',
            destinationName: destination?.title,
            departureId: searchDepartureId,
            checkIn: getSearchCheckIn(),
            nights: searchNights,
            rooms: 1,
            nationality: searchNationality,
            currency: searchCurrency,
            adults: searchAdults,
            children: 0,
            childrenAges: []
        };

        // HolidayPackage extends Hotel — pass the real package with its actual offers
        // This preserves offer IDs needed for getOfferDetails on hotel detail page
        const hotelData: Hotel = {
            id: pkg.id || pkg.productId || '',
            productId: pkg.productId || pkg.id || '',
            provider: pkg.provider || 1,
            name: pkg.name,
            location: pkg.location || { country: '', city: pkg.city || '' },
            rating: pkg.rating || 5,
            pricePerNight: pkg.pricePerNight || pkg.totalPrice || 0,
            currency: pkg.currency || currency,
            imageUrl: pkg.imageUrl || pkg.thumbnailUrl || '',
            thumbnailUrl: pkg.thumbnailUrl || pkg.imageUrl || '',
            board: pkg.board || '',
            roomType: pkg.roomType || '',
            themes: pkg.themes || [],
            facilities: pkg.facilities || [],
            offers: pkg.offers || [],  // Real offers from Tourvisio priceSearch
            description: pkg.description,
            images: pkg.images
        };

        navigate(generateHotelPath(hotelData), { state: { hotel: hotelData, searchQuery: query } });
    };

    // handlePremiumHotelClick removed — Premium Hotels now only on restaurant pages

    const handleBookFeatured = (hotel: FeaturedHotelDisplay) => {
        const query: SearchQuery = {
            type: SearchType.PackageTours,
            destinationId: destination?.destinationId || '',
            destinationName: destination?.title,
            departureId: '10', // Stockholm
            checkIn: hotel.checkIn,
            nights: hotel.duration,
            rooms: 1,
            nationality: 'SE', // FORCE SE
            currency: currency,
            adults: 2,
            children: 0,
            childrenAges: []
        };

        const partialHotel: Hotel = {
            id: hotel.hotelId,
            productId: hotel.hotelId,
            provider: 1,
            name: hotel.name,
            location: { country: '', city: hotel.city },
            rating: hotel.stars,
            pricePerNight: hotel.price || 0,
            currency: currency,
            imageUrl: hotel.imageUrl,
            thumbnailUrl: hotel.imageUrl,
            board: '',
            roomType: '',
            themes: [],
            facilities: [],
            offers: []
        };

        navigate(generateHotelPath(partialHotel), { state: { hotel: partialHotel, searchQuery: query } });
    };

    // Sort layout if exists - MUST be before any early return to maintain hooks order
    const sortedWidgets = useMemo(() => {
        if (!destination?.layout || !Array.isArray(destination.layout) || destination.layout.length === 0) return null;
        return [...destination.layout].sort((a, b) => a.order - b.order);
    }, [destination?.layout]);

    // Check if we have a Hero widget in layout
    const hasHeroWidget = sortedWidgets?.some(w => w.type === 'hero');

    if (!destination) return null;

    // --- Content Preparation ---

    // Fallback content if no layout
    const heroImg = destination.landingPage?.heroImage || destination.heroImage || destination.coverImage || destination.imageUrl || (apiImages.length > 0 ? apiImages[0] : 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=1200');
    const desc = destination.landingPage?.description?.[language] || destination.landingPage?.description?.en || destination.landingPage?.description?.sv || '';
    const historyText = destination.richContent?.history?.[language] || destination.richContent?.history?.en || destination.richContent?.history?.sv || '';
    const natureText = destination.richContent?.nature?.[language] || destination.richContent?.nature?.en || destination.richContent?.nature?.sv || '';

    // Context for widgets
    const translatedHeadingCity = language === 'sv' && destination.titleSv
        ? destination.titleSv
        : translateLocationName(destination.title, language);

    const cityOnly = translateLocationName(destination.title.split(',')[0], language);

    const widgetContext = {
        title: translatedHeadingCity,
        subtitle: desc,
        weather: destination.weather, // Pass weather data
        featuredHotels,
        loadingHotels,
        onBookHotel: handleBookFeatured,
        featuredTitle: language === 'sv' ? `Utvalda Hotell i ${cityOnly}` : language === 'da' ? `Udvalgte Hoteller i ${cityOnly}` : `Curated Hotels in ${cityOnly}`
    };

    return (
        <div className="min-h-screen bg-white">

            {/* 1. HERO - Either from Widget OR Default */}
            {hasHeroWidget ? (
                // If layout has hero, we let the loop render it, EXCEPT we might want it absolutely first.
                // For simplicity, we assume user ordered it first.
                // BUT, to prevent SearchWidget from being buried, we might need to assume Hero is first.
                null
            ) : (
                <HeroWidget
                    settings={{}}
                    content={{ image: heroImg, video: destination.heroVideo }}
                    title={widgetContext.title}
                    subtitle={widgetContext.subtitle || ''}
                />
            )}

            {/* 1.5 CTA BAR — below hero section */}
            {(destination as any).ctaBar?.enabled && (destination as any).ctaBar?.hotelName && (
                <div className="relative z-40">
                    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 shadow-xl">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-white text-2xl">🔥</span>
                                <p className="text-white font-bold text-lg">
                                    {language === 'sv'
                                        ? ((destination as any).ctaBar.text?.sv || (destination as any).ctaBar.text?.en || `Boka ${(destination as any).ctaBar.hotelName} nu!`)
                                        : language === 'da'
                                        ? ((destination as any).ctaBar.text?.da || (destination as any).ctaBar.text?.en || `Book ${(destination as any).ctaBar.hotelName} nu!`)
                                        : ((destination as any).ctaBar.text?.en || `Book ${(destination as any).ctaBar.hotelName} now!`)}
                                </p>
                            </div>
                            <button
                                onClick={() => navigate((destination as any).ctaBar.link || `/hotel/${(destination as any).ctaBar.hotelId}`)}
                                className="px-6 py-2.5 bg-white text-orange-600 rounded-full font-bold text-sm hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105">
                                {language === 'sv' ? 'Visa Erbjudande →' : language === 'da' ? 'Se Tilbud →' : 'View Offer →'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 1.5 DYNAMIC AD BANNER (Hero Style) */}
            {ads && ads.length > 0 && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                    <PageCTA cta={ads[0]} language={language} variant="hero" />
                </div>
            )}

            {/* 2. SEARCH WIDGET - Always Sticky/Fixed Position relative to Hero */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-30 -mt-24 mb-6 animate-fade-in-up">
                <div className="shadow-2xl rounded-3xl bg-white">
                    <SearchWidget
                        onSearch={handleSearch}
                        logApiCall={logApiCall}
                        isSettingsReady={isSettingsReady}
                        initialDestinationId={destination.destinationId}
                        initialQuery={state?.restoreQuery}
                    />
                </div>
            </div>

            {/* PARALLAX SECTION MENU */}
            {!sortedWidgets && (() => {
                const sectionConfig = (destination as any).sectionConfig || [];
                const getVisibility = (id: string) => {
                    const cfg = sectionConfig.find((s: any) => s.sectionId === id);
                    if (cfg) return cfg.visible !== false;
                    return true;
                };

                const DEFAULT_ORDER = [
                    'weather', 'airport_info', 'things_to_do', 'attractions', 'tour_packages',
                    'price_calendar', 'history', 'nature', 'itineraries', 'restaurants',
                    'api_hotels', 'featured_hotels', 'practical_info', 'blog'
                ];

                let menuOrderedSections: string[];
                if (sectionConfig.length > 0) {
                    const fromConfig = [...sectionConfig].sort((a: any, b: any) => a.order - b.order).map((s: any) => s.sectionId);
                    const missing = DEFAULT_ORDER.filter(id => !fromConfig.includes(id));
                    const practicalInfoIdx = fromConfig.indexOf('practical_info');
                    if (practicalInfoIdx !== -1 && missing.includes('airport_info')) {
                        fromConfig.splice(practicalInfoIdx, 0, 'airport_info');
                        missing.splice(missing.indexOf('airport_info'), 1);
                    }
                    menuOrderedSections = [...fromConfig, ...missing];
                } else {
                    menuOrderedSections = DEFAULT_ORDER;
                }

                const getSectionLabel = (sectionId: string): string | null => {
                    if (!getVisibility(sectionId)) return null;
                    switch (sectionId) {
                        case 'tour_packages': return tourPackages.length > 0 ? (language === 'sv' ? 'Paketresor' : language === 'da' ? 'Pakkerejser' : 'Packages') : null;
                        case 'things_to_do': return destination.sections?.thingsToDo?.length ? (language === 'sv' ? 'Att Göra' : language === 'da' ? 'Ting at gøre' : 'Things to Do') : null;
                        case 'attractions': return destination.sections?.attractions?.length ? (language === 'sv' ? 'Sevärdheter' : language === 'da' ? 'Attraktioner' : 'Attractions') : null;
                        case 'weather': return destination.weather ? (language === 'sv' ? 'Väder' : language === 'da' ? 'Vejr' : 'Weather') : null;
                        case 'history': return historyText ? (language === 'sv' ? 'Historia' : language === 'da' ? 'Historie' : 'History') : null;
                        case 'nature': return natureText ? (language === 'sv' ? 'Natur' : 'Nature') : null;
                        case 'itineraries': return destination.sections?.itineraries?.length ? (language === 'sv' ? 'Rundresor' : language === 'da' ? 'Rundrejser' : 'Itineraries') : null;
                        case 'restaurants': return destination.sections?.restaurants?.length ? (language === 'sv' ? 'Restauranger' : language === 'da' ? 'Restauranter' : 'Restaurants') : null;
                        case 'api_hotels': return apiHotels.length > 0 ? (language === 'sv' ? 'Alla Hotell' : language === 'da' ? 'Alle Hoteller' : 'All Hotels') : null;
                        case 'featured_hotels': return featuredHotels.length > 0 ? (language === 'sv' ? 'Utvalda Hotell' : language === 'da' ? 'Udvalgte Hoteller' : 'Featured Hotels') : null;
                        case 'practical_info': return destination.sections?.practicalInfo ? (language === 'sv' ? 'Praktisk Info' : language === 'da' ? 'Praktisk Info' : 'Practical Info') : null;
                        case 'airport_info': return destination.airportInfo ? (language === 'sv' ? 'Flygplats' : language === 'da' ? 'Lufthavn' : 'Airport') : null;
                        case 'blog': return true ? (language === 'sv' ? 'Reseguider' : language === 'da' ? 'Rejseguides' : 'Travel Guides') : null;
                        default: return null;
                    }
                };

                const visibleSections = menuOrderedSections
                    .map(id => ({ id, label: getSectionLabel(id) }))
                    .filter(s => s.label !== null);

                if (visibleSections.length === 0) return null;

                const handleScrollTo = (e: React.MouseEvent, id: string) => {
                    e.preventDefault();
                    const el = document.getElementById(`section-${id}`);
                    if (el) {
                        const y = el.getBoundingClientRect().top + window.scrollY - 80;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                };

                return (
                    <div className="sticky top-[60px] z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 py-3 mb-8 transition-all duration-300">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                            <div className="flex gap-6 whitespace-nowrap items-center">
                                {visibleSections.map(section => (
                                    <button 
                                      key={section.id} 
                                      onClick={(e) => handleScrollTo(e, section.id)}
                                      className="text-[13px] font-bold text-gray-500 hover:text-primary transition-colors uppercase tracking-widest px-1 py-1 border-b-2 border-transparent hover:border-primary"
                                    >
                                        {section.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* DYNAMIC SECTION RENDERING — respects sectionConfig ordering & visibility */}
            {(() => {
                // Section config from admin panel
                const sectionConfig = (destination as any).sectionConfig || [];
                const getVisibility = (id: string) => {
                    const cfg = sectionConfig.find((s: any) => s.sectionId === id);
                    if (cfg) return cfg.visible !== false;
                    return true;
                };

                // Default section order
                const DEFAULT_ORDER = [
                    'weather', 'airport_info', 'things_to_do', 'attractions', 'tour_packages',
                    'price_calendar', 'history', 'nature', 'itineraries', 'restaurants',
                    'api_hotels', 'featured_hotels', 'practical_info', 'blog'
                ];

                // Build ordered section list
                let orderedSections: string[];
                if (sectionConfig.length > 0) {
                    const fromConfig = [...sectionConfig].sort((a: any, b: any) => a.order - b.order).map((s: any) => s.sectionId);
                    
                    const missing = DEFAULT_ORDER.filter(id => !fromConfig.includes(id));

                    // Make sure airport_info is inserted before practical_info if missing
                    const practicalInfoIdx = fromConfig.indexOf('practical_info');
                    if (practicalInfoIdx !== -1 && missing.includes('airport_info')) {
                        fromConfig.splice(practicalInfoIdx, 0, 'airport_info');
                        missing.splice(missing.indexOf('airport_info'), 1);
                    }

                    orderedSections = [...fromConfig, ...missing];
                } else {
                    orderedSections = DEFAULT_ORDER;
                }

                // Handle hotel click helper
                const onHotelClick = (hotel: any) => {
                    const query: SearchQuery = {
                        searchId: hotel.searchId,
                        type: SearchType.PackageTours,
                        destinationId: destination.destinationId,
                        destinationName: destination.title,
                        departureId: searchDepartureId,
                        checkIn: getSearchCheckIn(),
                        nights: searchNights,
                        rooms: 1,
                        nationality: searchNationality,
                        currency: searchCurrency,
                        adults: searchAdults,
                        children: 0,
                        childrenAges: []
                    };
                    const hotelData: Hotel = {
                        id: hotel.id,
                        productId: hotel.id,
                        provider: hotel.provider || 1,
                        name: hotel.name,
                        location: hotel.location || { country: '', city: hotel.city },
                        rating: hotel.stars || hotel.rating || 5,
                        pricePerNight: hotel.price || hotel.pricePerNight || 0,
                        currency: hotel.currency || currency,
                        imageUrl: hotel.imageUrl,
                        thumbnailUrl: hotel.imageUrl,
                        board: hotel.board || '',
                        roomType: hotel.roomType || '',
                        themes: hotel.themes || [],
                        facilities: hotel.facilities || [],
                        offers: hotel.offers || [],
                        description: hotel.description,
                        images: hotel.images
                    };
                    navigate(generateHotelPath(hotelData), { state: { hotel: hotelData, searchQuery: query } });
                };

                // Section renderer map
                const renderSection = (sectionId: string) => {
                    if (!getVisibility(sectionId)) return null;
                    const renderCityName = translateLocationName(destination.title.split(',')[0], language);
                    switch (sectionId) {
                        case 'tour_packages':
                            return <TourPackagesSection key={sectionId} packages={tourPackages} loading={loadingTourPackages} destinationName={renderCityName} currency={currency} onPackageClick={handleTourPackageClick} />;
                        // premium_hotels removed — now only on restaurant pages as 'nearby hotels'
                        case 'price_calendar':
                            // DISABLED — Price Calendar preserved in PriceCalendar.tsx for future reactivation
                            // return <PriceCalendar key={sectionId} destinationId={(destination as any).destinationId || slug || ''} destinationSlug={slug || ''} destinationName={destination.title.split(',')[0]} useCacheData={(destination as any).useCacheData !== false} />;
                            return null;
                        case 'things_to_do':
                            return destination.sections?.thingsToDo?.length ? <ThingsToDoSection key={sectionId} activities={destination.sections.thingsToDo} /> : null;
                        case 'attractions':
                            return destination.sections?.attractions?.length ? <AttractionsSection key={sectionId} attractions={destination.sections.attractions} /> : null;
                        case 'weather':
                            return destination.weather ? <WeatherWidget key={sectionId} weatherData={destination.weather} /> : null;
                        case 'history':
                            return historyText ? <RichTextWidget key={sectionId} content={{ title: language === 'sv' ? 'Historia & Kultur' : language === 'da' ? 'Historie & Kultur' : 'History & Culture', text: historyText, image: apiImages.length > 1 ? apiImages[1] : (destination.gallery?.[0] || destination.imageUrl) }} settings={{ alignment: 'left', background: 'white' }} /> : null;
                        case 'nature':
                            return natureText ? <RichTextWidget key={sectionId} content={{ title: language === 'sv' ? 'Natur & Landskap' : language === 'da' ? 'Natur & Landskab' : 'Nature & Landscape', text: natureText, image: apiImages.length > 2 ? apiImages[2] : (destination.gallery?.[1] || 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?q=80&w=1200') }} settings={{ alignment: 'right', background: 'gray' }} /> : null;
                        case 'itineraries':
                            return destination.sections?.itineraries?.length ? <ItinerariesSection key={sectionId} itineraries={destination.sections.itineraries} /> : null;
                        case 'restaurants':
                            return destination.sections?.restaurants?.length ? <RestaurantsSection key={sectionId} restaurants={destination.sections.restaurants} /> : null;
                        case 'api_hotels':
                            return <HotelsSection key={sectionId} hotels={apiHotels} loading={loadingApiHotels} destinationName={renderCityName} onHotelClick={onHotelClick} />;
                        case 'featured_hotels':
                            return (featuredHotels.length > 0 || loadingHotels) ? <HotelGridWidget key={sectionId} hotels={featuredHotels} title={widgetContext.featuredTitle} loading={loadingHotels} onBook={handleBookFeatured} /> : null;
                        case 'practical_info':
                            return destination.sections?.practicalInfo ? <PracticalInfoSection key={sectionId} info={destination.sections.practicalInfo} /> : null;
                        case 'airport_info':
                            return destination.airportInfo ? <AirportInfoSection key={sectionId} info={destination.airportInfo} /> : null;
                        case 'blog':
                            return <BlogPostsSection key={sectionId} destinationSlug={slug || ''} destinationName={renderCityName} />
                        default:
                            return null;
                    }
                };

                // If layout widgets exist, render them instead of sections
                if (sortedWidgets) {
                    return (
                        <div className="flex flex-col">
                            {sortedWidgets.map(widget => (
                                <WidgetRenderer key={widget.id} widget={widget} context={widgetContext} />
                            ))}
                        </div>
                    );
                }

                return (
                    <div className="flex flex-col">
                        {orderedSections.map((sectionId: string) => {
                            const sectionContent = renderSection(sectionId);
                            if (!sectionContent) return null;
                            return (
                                <div key={sectionId} id={`section-${sectionId}`}>
                                    {sectionContent}
                                </div>
                            );
                        })}
                    </div>
                );
            })()}
        </div>
    );
};
